import { ROLE_TEAM } from './role-types';
import type { Role } from './role-types';
import type { GameSettings, RolePreset } from './types';

export const ROLE_PRESETS: RolePreset[] = [
  { id: 'p5', label: '5 игроков · 1 мафия', minPlayers: 5, maxPlayers: 5, composition: { mafia_don: 1, mafia: 0, doctor: 1, detective: 1, prostitute: 0, civilian: 2 } },
  { id: 'p6', label: '6 игроков · 1 мафия', minPlayers: 6, maxPlayers: 6, composition: { mafia_don: 1, mafia: 0, doctor: 1, detective: 1, prostitute: 0, civilian: 3 } },
  { id: 'p7', label: '7 игроков · 2 мафии', minPlayers: 7, maxPlayers: 7, composition: { mafia_don: 1, mafia: 1, doctor: 1, detective: 1, prostitute: 0, civilian: 3 } },
  { id: 'p8', label: '8 игроков · 2 мафии', minPlayers: 8, maxPlayers: 8, composition: { mafia_don: 1, mafia: 1, doctor: 1, detective: 1, prostitute: 0, civilian: 4 } },
  { id: 'p9', label: '9 игроков · 2 мафии', minPlayers: 9, maxPlayers: 9, composition: { mafia_don: 1, mafia: 1, doctor: 1, detective: 1, prostitute: 0, civilian: 5 } },
  { id: 'p10', label: '10 игроков · 3 мафии', minPlayers: 10, maxPlayers: 10, composition: { mafia_don: 1, mafia: 2, doctor: 1, detective: 1, prostitute: 0, civilian: 5 } },
  { id: 'p11', label: '11 игроков · 3 мафии', minPlayers: 11, maxPlayers: 11, composition: { mafia_don: 1, mafia: 2, doctor: 1, detective: 1, prostitute: 0, civilian: 6 } },
  { id: 'p12', label: '12 игроков · 3 мафии', minPlayers: 12, maxPlayers: 12, composition: { mafia_don: 1, mafia: 2, doctor: 1, detective: 1, prostitute: 0, civilian: 7 } },
];

export function presetForCount(playerCount: number): RolePreset {
  return (
    ROLE_PRESETS.find((p) => playerCount >= p.minPlayers && playerCount <= p.maxPlayers) ??
    ROLE_PRESETS[ROLE_PRESETS.length - 1]
  );
}

export function validateComposition(composition: Record<Role, number>, playerCount: number): string[] {
  const errors: string[] = [];
  const total = (Object.keys(composition) as Role[]).reduce((sum, r) => sum + (composition[r] ?? 0), 0);
  if (total !== playerCount) errors.push(`Сумма ролей (${total}) должна равняться числу игроков (${playerCount}).`);
  const totalMafia = (Object.keys(composition) as Role[]).filter((r) => ROLE_TEAM[r] === 'mafia').reduce((s, r) => s + composition[r], 0);
  if (totalMafia < 1) errors.push('Нужна хотя бы одна мафия.');
  const livingTown = (Object.keys(composition) as Role[]).filter((r) => ROLE_TEAM[r] === 'town').reduce((s, r) => s + composition[r], 0);
  if (livingTown < 1) errors.push('Нужен хотя бы один горожанин.');
  return errors;
}

/**
 * Final role composition for a game: start from the preset, then apply the
 * per-room toggles for the doctor and the prostitute.
 */
export function compositionForGame(preset: RolePreset, settings: GameSettings): Record<Role, number> {
  const comp: Record<Role, number> = {
    mafia_don: preset.composition.mafia_don ?? 0,
    mafia: preset.composition.mafia ?? 0,
    doctor: preset.composition.doctor ?? 0,
    detective: preset.composition.detective ?? 0,
    prostitute: 0,
    civilian: preset.composition.civilian ?? 0,
  };

  if (!settings.includeDoctor && comp.doctor > 0) {
    comp.civilian += comp.doctor;
    comp.doctor = 0;
  }

  if (settings.includeProstitute && comp.civilian > 0) {
    comp.civilian -= 1;
    comp.prostitute = 1;
  }

  return comp;
}

export const DEFAULT_SETTINGS: GameSettings = {
  presetId: 'p7',
  discussionSeconds: 60,
  votingSeconds: 45,
  nightActionSeconds: 60,
  roleRevealSeconds: 10,
  doctorCanSelfSave: true,
  revealRoleOnElimination: false,
  audioEnabled: true,
  includeDoctor: true,
  includeProstitute: false,
  startWithDay: false,
  autoNight: true,
  manualVoting: true,
};

export const DEFAULT_DURATION_SECONDS = {
  discussion: 60,
  voting: 45,
  nightAction: 60,
  nightStart: 6,
  nightSleep: 5,
  roleReveal: 10,
  dayStart: 30,
  nightResolution: 8,
  dayResolution: 5,
  tiebreak: 60,
  lastWords: 30,
} as const;

/** Hard ceiling for the ack-based role reveal before it auto-advances. */
export const ROLE_REVEAL_SAFETY_SECONDS = 120;
/** Countdown shown on each device after every player taps "Понятно". */
export const ROLE_REVEAL_ACK_COUNTDOWN_SECONDS = 5;

/**
 * Narration scripts for each phase. Used as the TTS fallback when no real audio
 * asset is mapped to the track id.
 */
export const NARRATION: Record<string, string> = {
  roles_revealed: 'Роли розданы. Посмотрите свою роль и нажмите Понятно.',
  game_starting: 'Все готовы. Игра начинается.',
  night_city_sleep: 'Город засыпает. Все закрывают глаза. Наступает ночь.',
  mafia_wake: 'Просыпается мафия. Выбирайте жертву.',
  mafia_confirm_reminder: 'Мафии нужно всем нажать Подтвердить на одного и того же игрока.',
  mafia_don_wake: 'Просыпается дон мафии. Выбирайте, кого проверить.',
  doctor_wake: 'Просыпается доктор. Выбирайте, кого спасти этой ночью.',
  detective_wake: 'Просыпается комиссар. Проверяйте подозреваемого.',
  mafia_sleep: 'Вся мафия засыпает.',
  mafia_don_sleep: 'Дон мафии засыпает.',
  doctor_sleep: 'Доктор засыпает.',
  detective_sleep: 'Комиссар засыпает.',
  morning_comes: 'Город просыпается. Наступил день.',
  city_discussion: 'Начинается высказывание мнения. Каждый по очереди говорит.',
  voting_begins: 'Высказывание мнения окончено. Начинается голосование.',
  tiebreak: 'Голоса разделились поровну. Голосуйте: убрать обоих или оставить обоих.',
  last_words: 'Игрок получает 30 секунд на последнее слово.',
};

export function settingsFromPreset(preset: RolePreset): GameSettings {
  return { ...DEFAULT_SETTINGS, presetId: preset.id };
}
